import type { DealTag } from '@platform/shared-types';
import type { ActiveDealVersionSummary } from '../../services/dealsApi';
import type { Deal } from '../../services/dealPricing';

/**
 * THE DEALS LIST, DECIDED HERE.
 *
 * Kept out of the component for the reason the rest of this folder is: the repo
 * runs plain vitest with no DOM renderer, so anything left inside a component
 * is untestable. Every rule below — which price a row quotes, what a tag means,
 * where a variant hangs in the tree — is decided in this file.
 */

// ---------------------------------------------------------------------------
// WHAT A DEAL SELLS — the published version, or the row as an implicit v1
// ---------------------------------------------------------------------------

/**
 * The commercial fields a row prices from, and WHERE they came from.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE DEAL ROW IS NOT WHAT A DEAL SELLS.
 * ─────────────────────────────────────────────────────────────────────────
 * A deal row is the working copy. Editing a versioned deal writes a DRAFT, and
 * the draft's price is not what anybody can sign — the active PUBLISHED version
 * is. A list that quoted the row would advertise unpublished numbers, and would
 * do it most loudly for the deals under active revision.
 *
 * So the version wins wherever a deal has one. The row is used for exactly one
 * case: a deal that has never published, which is its own implicit v1 and is
 * the normal state of every deal predating versioning. `source` records which
 * happened, so a row can say so rather than leaving an operator to guess which
 * number they are looking at.
 */
export interface EffectiveDealTerms {
  source: 'published' | 'unversioned';
  activeVersionNo: number | null;
  hasDraft: boolean;
  priceCents: number;
  devicePackageIds: string[];
  deviceTerms: Record<string, any>;
}

export function effectiveDealTerms(
  deal: Deal,
  version: ActiveDealVersionSummary | undefined,
): EffectiveDealTerms {
  const rowPrice = typeof deal.priceCents === 'number'
    ? deal.priceCents
    : (typeof deal.monthlyAmount === 'number' ? deal.monthlyAmount : 0);

  if (version && !version.implicit) {
    return {
      source: 'published',
      activeVersionNo: version.activeVersionNo,
      hasDraft: version.hasDraft,
      /**
       * `publishDraft` refuses to publish without a price, so a published
       * version always has one. The fallback covers only a row frozen before
       * that rule existed — and it falls back to the DEAL's price rather than
       * to zero, because a deal quoted at $0 reads as free.
       */
      priceCents: typeof version.priceCents === 'number' ? version.priceCents : rowPrice,
      devicePackageIds: version.devicePackageIds || [],
      deviceTerms: version.deviceTerms || {},
    };
  }

  return {
    source: 'unversioned',
    activeVersionNo: null,
    hasDraft: version?.hasDraft ?? false,
    priceCents: rowPrice,
    // `devicePackageIds` and `deviceSkuIds` are the same list under two names —
    // see `resolveDeviceSkuIds` in super-admin-ext. Read either; never union
    // them, or a device removed under one name reappears under the other.
    devicePackageIds: (deal as any).devicePackageIds || (deal as any).deviceSkuIds || [],
    deviceTerms: (deal as any).deviceTerms || {},
  };
}

// ---------------------------------------------------------------------------
// TAGS
// ---------------------------------------------------------------------------

/** Ids are minted per tag and never reused — a deleted tag's id may still sit on old deals. */
export const newTagId = (): string =>
  `tag_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * The tags a deal actually shows, resolved against the registry.
 *
 * An id with no tag behind it is DROPPED, silently and by design: that is how
 * deleting a tag works. The registry is the only delete, so an orphaned id on
 * an old deal row is invisible rather than rendering as a blank chip. See
 * `deal-tags-put.ts` for why the delete does not rewrite deals.
 */
export function resolveDealTags(deal: Deal, registry: DealTag[]): DealTag[] {
  const byId = new Map(registry.map((t) => [t.id, t]));
  const ids: string[] = Array.isArray((deal as any).tags) ? (deal as any).tags : [];
  return ids.map((id) => byId.get(id)).filter((t): t is DealTag => Boolean(t));
}

/**
 * Readable text for a tag's fill. Tags are authored as arbitrary hex, so a
 * stored foreground colour would be one more thing that can drift out of
 * contrast with its background; this derives it instead.
 *
 * Rec. 601 luma, thresholded at 60% — the sRGB weighting that matches how the
 * eye reads brightness, rather than a flat channel average that calls yellow
 * dark and blue light.
 */
export function tagTextColor(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#1c1917';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255; const g = (n >> 8) & 255; const b = n & 255;
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.6 ? '#1c1917' : '#ffffff';
}

// ---------------------------------------------------------------------------
// THE FAMILY TREE
// ---------------------------------------------------------------------------

export interface DealNode {
  deal: Deal;
  /** 0 for a master; 1 for a copy of it; 2 for a copy of that, and so on. */
  depth: number;
  children: DealNode[];
  /** Every descendant, not just direct children — what the "N variants" count says. */
  descendantCount: number;
}

/**
 * Deals arranged into families.
 *
 * A ROOT is any deal with no `parentDealId` — or whose parent is not in the set
 * being displayed. That second clause is what keeps a filtered list honest: a
 * variant whose master was filtered out is shown as its own root rather than
 * silently dropped for want of somewhere to hang.
 *
 * ⚠️ A CYCLE CANNOT HIDE A DEAL. Lineage is written once by the duplicate
 * endpoint and a copy always points at something older, so a cycle should be
 * impossible — but "should be impossible" is how deals go missing from an admin
 * screen. Anything not reached by the walk is appended as a root at the end.
 */
export function buildDealTree(deals: Deal[]): DealNode[] {
  const byId = new Map(deals.map((d) => [d.id, d]));
  const childrenOf = new Map<string, Deal[]>();
  const roots: Deal[] = [];

  for (const deal of deals) {
    const parentId = (deal as any).parentDealId as string | undefined;
    if (parentId && byId.has(parentId) && parentId !== deal.id) {
      childrenOf.set(parentId, [...(childrenOf.get(parentId) || []), deal]);
    } else {
      roots.push(deal);
    }
  }

  const seen = new Set<string>();
  const build = (deal: Deal, depth: number): DealNode => {
    seen.add(deal.id);
    const children = (childrenOf.get(deal.id) || [])
      .filter((c) => !seen.has(c.id))
      .map((c) => build(c, depth + 1));
    return {
      deal,
      depth,
      children,
      descendantCount: children.reduce((n, c) => n + 1 + c.descendantCount, 0),
    };
  };

  const tree = roots.map((r) => build(r, 0));
  // Nothing may vanish. A deal unreachable from any root (only possible via a
  // cycle) becomes a root of its own.
  for (const deal of deals) {
    if (!seen.has(deal.id)) tree.push(build(deal, 0));
  }
  return tree;
}

/** Depth-first flatten — the order rows are rendered in once a family is open. */
export function flattenTree(nodes: DealNode[]): DealNode[] {
  const out: DealNode[] = [];
  const walk = (n: DealNode) => { out.push(n); n.children.forEach(walk); };
  nodes.forEach(walk);
  return out;
}

// ---------------------------------------------------------------------------
// FILTERING
// ---------------------------------------------------------------------------

export type StatusFilter = 'all' | 'active' | 'inactive' | 'landing' | 'partner';

export interface DealFilter {
  search: string;
  /** Tag ids. Empty = no tag filter. A deal matches if it carries ANY of them. */
  tagIds: string[];
  status: StatusFilter;
}

export function dealMatches(deal: Deal, filter: DealFilter): boolean {
  const q = filter.search.trim().toLowerCase();
  if (q) {
    const haystack = `${deal.name || ''} ${deal.code || ''} ${(deal as any).landingPageLabel || ''}`.toLowerCase();
    if (!haystack.includes(q)) return false;
  }

  if (filter.tagIds.length > 0) {
    const tags: string[] = Array.isArray((deal as any).tags) ? (deal as any).tags : [];
    if (!filter.tagIds.some((id) => tags.includes(id))) return false;
  }

  switch (filter.status) {
    case 'active': return deal.isActive === 'true';
    case 'inactive': return deal.isActive !== 'true';
    case 'landing': return Boolean((deal as any).showOnLandingPage);
    case 'partner': return !(deal as any).showOnLandingPage;
    default: return true;
  }
}

/**
 * A MATCHING VARIANT KEEPS ITS ANCESTORS.
 *
 * Searching for a variant by name has to return something you can see it in
 * context, so a master is kept when a descendant matches even though the master
 * itself does not — otherwise the hit renders as an orphan with no indication
 * of which family it belongs to. Ancestors kept this way are marked, so the row
 * can be drawn as context rather than as a result.
 */
export function filterTree(
  nodes: DealNode[],
  filter: DealFilter,
): { nodes: DealNode[]; contextIds: Set<string>; matchCount: number } {
  const contextIds = new Set<string>();
  let matchCount = 0;

  const prune = (node: DealNode): DealNode | null => {
    const children = node.children.map(prune).filter((c): c is DealNode => c !== null);
    const selfMatches = dealMatches(node.deal, filter);
    if (selfMatches) matchCount += 1;
    if (!selfMatches && children.length === 0) return null;
    if (!selfMatches) contextIds.add(node.deal.id);
    return {
      ...node,
      children,
      descendantCount: children.reduce((n, c) => n + 1 + c.descendantCount, 0),
    };
  };

  return { nodes: nodes.map(prune).filter((n): n is DealNode => n !== null), contextIds, matchCount };
}

// ---------------------------------------------------------------------------
// GROUPING
// ---------------------------------------------------------------------------

export interface DealGroup {
  key: string;
  /** Null for the "Untagged" bucket. */
  tag: DealTag | null;
  nodes: DealNode[];
  dealCount: number;
  variantCount: number;
}

/**
 * Families bucketed by tag, for the grouped view.
 *
 * A family belongs to a group by its ROOT's tags: a master tagged "Retail" with
 * a thousand variants reads as one Retail family, which is how an operator
 * thinks about it. A deal carrying several tags appears under each of them —
 * the same family, filed twice, which is what multiple tags are for.
 *
 * A root with no resolvable tag lands in "Untagged" rather than nowhere. That
 * bucket is why the grouped view can never hide a deal, and why an operator can
 * find the deals still needing a tag.
 */
export function groupByTag(nodes: DealNode[], registry: DealTag[]): DealGroup[] {
  const groups = new Map<string, DealGroup>();
  const groupFor = (key: string, tag: DealTag | null): DealGroup => {
    let g = groups.get(key);
    if (!g) { g = { key, tag, nodes: [], dealCount: 0, variantCount: 0 }; groups.set(key, g); }
    return g;
  };

  for (const node of nodes) {
    const tags = resolveDealTags(node.deal, registry);
    const targets = tags.length > 0
      ? tags.map((t) => groupFor(t.id, t))
      : [groupFor('__untagged__', null)];
    for (const g of targets) {
      g.nodes.push(node);
      g.dealCount += 1;
      g.variantCount += node.descendantCount;
    }
  }

  // Registry order, so the rail and the sections read top-to-bottom the same
  // way; Untagged last because it is a residue, not a category.
  const order = new Map(registry.map((t, i) => [t.id, i]));
  return [...groups.values()].sort((a, b) => {
    if (a.tag === null) return 1;
    if (b.tag === null) return -1;
    return (order.get(a.tag.id) ?? 0) - (order.get(b.tag.id) ?? 0);
  });
}
